import type { ConstantsLoginStruct } from "@gosaas/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { AuthPage } from "@/components/auth/auth-page";
import { useAuth } from "@/components/providers/auth";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import {
	deleteMfaEmail,
	deleteMfaToken,
	getMfaEmail,
	getMfaToken,
	setAccessToken,
	setRefreshToken,
} from "@/utils/local-storage";
import { seo } from "@/utils/seo";

const MfaSchema = z
	.object({
		otp: z.string().optional(),
		recoveryCode: z.string().optional(),
	})
	.refine((data) => data.otp || data.recoveryCode, {
		message: "Either MFA code or recovery code is required",
	});

export const Route = createFileRoute("/auth/mfa/required")({
	component: RouteComponent,
	loader: async () => {
		const email = getMfaEmail();
		const mfaToken = getMfaToken();
		return { email, mfaToken };
	},
	head: () =>
		seo({
			title: "MFA Required",
			description: "Enter your MFA code or recovery code",
			keywords: "mfa, two-factor, authentication, security",
		}),
});

export function RouteComponent() {
	const router = useRouter();
	const { refresh } = useAuth();
	const { email, mfaToken } = Route.useLoaderData();

	const form = useForm<z.infer<typeof MfaSchema>>({
		resolver: zodResolver(MfaSchema),
		defaultValues: {
			otp: "",
			recoveryCode: "",
		},
	});

	const onSubmit = async (data: z.infer<typeof MfaSchema>) => {
		if (!mfaToken) {
			toast.error("Your sign-in session has expired", {
				description: "Please sign in again to continue.",
			});
			router.navigate({ to: "/auth/login" });
			return;
		}
		try {
			const response = await requestWithHandling<ConstantsLoginStruct>(() =>
				apiClient.api.authMfaValidateCreate({
					mfa_token: mfaToken,
					otp: data.otp,
					recovery_key: data.recoveryCode,
				}),
			);

			if (response.data?.access_token && response.data?.refresh_token) {
				setAccessToken(response.data.access_token);
				setRefreshToken(response.data.refresh_token);
				deleteMfaEmail();
				deleteMfaToken();
				toast.success("Logged in successfully", {
					description: "Welcome back!",
				});
				refresh();
				router.navigate({ to: "/dashboard" });
			}
		} catch (error) {
			if (error instanceof TypedClientApiError) {
				if (error.code === "MFA_CODE_INVALID") {
					toast.error("That code didn't work", {
						description: "Please check your code and try again.",
					});
					return;
				}
				if (error.code === "MFA_RECOVERY_KEY_INVALID") {
					toast.error("That recovery code didn't work", {
						description: "Please check your recovery code and try again.",
					});
					return;
				}
				if (error.code === "VERIFICATION_ATTEMPTS_EXCEEDED") {
					toast.error("Too many failed attempts", {
						description: "Please wait a few minutes before trying again.",
					});
					return;
				}
				if (error.code === "INVALID_TOKEN") {
					deleteMfaEmail();
					deleteMfaToken();
					toast.error("Your sign-in session has expired", {
						description: "Please sign in again to continue.",
					});
					router.navigate({ to: "/auth/login" });
					return;
				}
			}
			toast.error("Couldn't verify your identity", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Two-factor authentication"
			description="Enter the code from your authenticator app or a recovery code."
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					{email ? (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input
									type="email"
									value={email}
									disabled
									readOnly
									autoComplete="email"
								/>
							</FormControl>
						</FormItem>
					) : null}

					<div className="space-y-3">
						<Tabs defaultValue="app" className="w-full">
							<TabsList className="grid h-9 w-full grid-cols-2">
								<TabsTrigger value="app" type="button">
									App code
								</TabsTrigger>
								<TabsTrigger value="recovery" type="button">
									Recovery
								</TabsTrigger>
							</TabsList>
							<TabsContent value="app" className="mt-4">
								<FormField
									control={form.control}
									name="otp"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Authentication code</FormLabel>
											<FormControl>
												<InputOTP
													maxLength={6}
													value={field.value}
													onChange={(value) => field.onChange(value)}
												>
													<InputOTPGroup>
														<InputOTPSlot index={0} />
														<InputOTPSlot index={1} />
														<InputOTPSlot index={2} />
													</InputOTPGroup>
													<InputOTPSeparator />
													<InputOTPGroup>
														<InputOTPSlot index={3} />
														<InputOTPSlot index={4} />
														<InputOTPSlot index={5} />
													</InputOTPGroup>
												</InputOTP>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</TabsContent>
							<TabsContent value="recovery" className="mt-4">
								<FormField
									control={form.control}
									name="recoveryCode"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Recovery code</FormLabel>
											<FormControl>
												<Input
													placeholder="Recovery code"
													className="font-mono"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</TabsContent>
						</Tabs>
					</div>

					<Button type="submit" className="h-11 w-full cursor-pointer">
						Continue
					</Button>
				</form>
			</Form>

			<p className="mt-8 text-center text-base text-muted-foreground">
				<Link
					to="/auth/login"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Back to sign in
				</Link>
			</p>
		</AuthPage>
	);
}
